import React, { useEffect, useState } from "react";
import { api, resolveImage } from "../lib/api";
import { Plus, Star, Sparkles } from "lucide-react";
import { useCart } from "../context/CartContext";

export const FeaturedSpotlight = () => {
  const [featured, setFeatured] = useState(null);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    let mounted = true;
    api
      .get("/products")
      .then((res) => {
        if (!mounted) return;
        const f = res.data.find((p) => p.is_featured) || null;
        setFeatured(f);
      })
      .catch(() => {})
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  if (loading || !featured) return null;
  const soldOut = featured.in_stock === false;

  return (
    <section
      id="featured"
      className="w-full px-6 pt-4 pb-12 md:pt-8 md:pb-20"
      style={{ backgroundColor: "var(--bg-primary)" }}
      data-testid="featured-spotlight"
    >
      <div className="max-w-6xl mx-auto">
        <div
          className="relative rounded-[2rem] overflow-hidden grid md:grid-cols-2 gap-0"
          style={{
            backgroundColor: "var(--bg-secondary)",
            border: "1px solid var(--border-soft)",
            boxShadow: "0 20px 50px -24px rgba(74,93,78,0.25)",
          }}
        >
          {/* Decorative sparkle */}
          <Sparkles
            size={28}
            className="absolute top-4 right-4 z-10"
            style={{ color: "var(--brand-orange)" }}
          />

          {/* Image */}
          <div
            className="aspect-square md:aspect-auto md:min-h-[420px] overflow-hidden"
            style={{ backgroundColor: "var(--bg-tertiary)" }}
          >
            <img
              src={resolveImage(featured.image_url)}
              alt={featured.name}
              className="w-full h-full object-cover transition-transform duration-700 hover:scale-105"
              style={{ filter: soldOut ? "grayscale(0.7)" : "none" }}
              data-testid="featured-product-image"
            />
          </div>

          {/* Content */}
          <div className="p-8 md:p-12 flex flex-col justify-center">
            <div className="flex items-center gap-2 mb-4">
              <span
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold"
                style={{
                  backgroundColor: "var(--brand-peach)",
                  color: "var(--text-accent)",
                }}
              >
                <Star size={12} fill="currentColor" />
                This Week’s Pick
              </span>
              {soldOut && (
                <span
                  className="inline-flex items-center text-[10px] uppercase tracking-[0.2em] font-semibold px-3 py-1 rounded-full"
                  style={{
                    backgroundColor: "#fff",
                    color: "var(--brand-orange)",
                    border: "1px solid var(--brand-orange)",
                  }}
                >
                  Sold Out
                </span>
              )}
            </div>

            <p
              className="font-script text-2xl md:text-3xl mb-1"
              style={{ color: "var(--text-accent)" }}
            >
              meet your new friend
            </p>
            <h2
              className="font-display text-4xl md:text-5xl leading-tight mb-4"
              style={{ color: "var(--text-primary)" }}
              data-testid="featured-product-name"
            >
              {featured.name}
            </h2>
            <p
              className="text-base md:text-lg leading-relaxed mb-6"
              style={{ color: "var(--text-secondary)" }}
              data-testid="featured-product-description"
            >
              {featured.description}
            </p>

            <div className="flex items-center gap-5 flex-wrap">
              <span
                className="font-display text-4xl md:text-5xl"
                style={{ color: "var(--brand-sage)" }}
                data-testid="featured-product-price"
              >
                ${Number(featured.price).toFixed(2)}
              </span>
              <button
                onClick={() => !soldOut && addItem(featured)}
                disabled={soldOut}
                className="btn-sage inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-sm font-semibold tracking-wide uppercase disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="featured-add-to-cart-button"
              >
                <Plus size={16} />
                {soldOut ? "Sold Out" : "Add to Cart"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeaturedSpotlight;
